import {
  ColumnDef,
  ExpandedState,
  getCoreRowModel,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  useReactTable
} from "@tanstack/react-table";
import { Checkbox, Show } from "shared/components";

import { Table } from "shared/app/Table";
import type { UntitledIcon } from "shared/app/Table/type";
import {
  Archive,
  ArrowRight,
  File06,
  FileX03,
  SearchSM,
  User01,
  Users01
} from "untitledui-js-base";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ApplicantStatus,
  ApplicationFeeStatus,
  ApplicationStatus,
  Invitee
} from "shared/types";
import {
  Dispatch,
  lazy,
  ReactNode,
  SetStateAction,
  Suspense,
  useEffect,
  useMemo,
  useState
} from "react";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import { Applicant, Application } from "types/application";

import { useAuthContext } from "providers/Auth";
import { RuleBaseGraph } from "shared/app/RuleBaseGraph";
import { ScoreGraphSize } from "views/applications-id/components/ApplicationScoreCard/constants";
import { getFullName } from "shared/utils/get-full-name";
import {
  ApplicationLike,
  getApplicationUIStatus
} from "utils/get-application-ui-status";
import { Loader } from "shared/components/Loader";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { ScreeningStatusBadge } from "../ScreeningStatusBadge";
import { ActiveTab, newOrder } from "./constants";
import {
  formatCombinedNamesAndEmails,
  getApplicantNames,
  getInviteesEmails
} from "./helpers";
import {
  CashbackIcon,
  PathfinderDivideIcon,
  PauseIcon,
  PlayIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import {
  placeOnHoldApplication,
  resumeApplication
} from "shared/app/OnHoldResume/api";
import { useQueryClient } from "@tanstack/react-query";
import {
  getApplicationSplittingDisabled,
  getApplicationSplittingTooltip
} from "../../utils/applicationSplittingUtils";
import { DateTimeTableCell } from "shared/components/DateTimeTableCell";
import {
  camelToSentence,
  capitalize,
  underscoreToSentence
} from "shared/utils";

const ScoreGraph = lazy(
  () =>
    import("views/applications-id/components/ApplicationScoreCard/ScoreGraph")
);

const EmptyStateFile06Icon: UntitledIcon = (props) => <File06 {...props} />;
const EmptyStateSearchIcon: UntitledIcon = (props) => <SearchSM {...props} />;
const EmptyStateFileXIcon: UntitledIcon = (props) => <FileX03 {...props} />;

type ApplicationWithUIStatus = Application & {
  ui_status: ApplicationStatus;
};

export const TableApplications = ({
  groupBy,
  applications,
  onInviteClick,
  activeTab,
  hasSearchParams,
  isLoading,
  setSelectedRentersRow,
  selectedRentersRow,
  onCancelClick,
  onUncancelClick,
  onMoveClick,
  onRefundClick,
  onSplitClick,
  sorting,
  setSorting,
  sortableKeys,
  columnsVisibility,
  invalidateQueries
}: {
  groupBy?: string;
  applications: Application[];
  onInviteClick?: () => void;
  activeTab: ActiveTab;
  isPending?: boolean;
  isCanceled?: boolean;
  hasSearchParams?: boolean;
  setSelectedRentersRow: OnChangeFn<RowSelectionState>;
  selectedRentersRow: RowSelectionState;
  onCancelClick?: (id: string) => void;
  onUncancelClick?: (id: string) => void;
  onMoveClick?: (id: string) => void;
  onRefundClick?: (id: string) => void;
  onSplitClick?: (id: string) => void;
  isLoading?: boolean;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  sortableKeys: string[];
  invalidateQueries?: unknown[][];
  columnsVisibility: { id: string; checked: boolean; disabled: boolean }[];
}) => {
  const { Unit, unit, Property } = useUnitsPropertiesDictionary();
  const { pathname } = useLocation();
  const { type, permissions } = useAuthContext();
  const { adminPrivileges } = type;
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const tableApplications = useMemo<ApplicationWithUIStatus[]>(() => {
    return applications.map((application) => ({
      ...application,
      ui_status: getApplicationUIStatus(
        application as unknown as ApplicationLike
      )
    }));
  }, [applications]);

  const currentTab = {
    isAll: activeTab === ActiveTab.All,
    isNotSubmitted: activeTab === ActiveTab.NotSubmitted,
    isPartiallySubmitted: activeTab === ActiveTab.PartiallySubmitted,
    isSubmitted: activeTab === ActiveTab.Submitted,
    isApproved: activeTab === ActiveTab.Approved,
    isConditionallyApproved: activeTab === ActiveTab.ApprovedConditionally,
    isDeclined: activeTab === ActiveTab.Declined,
    isCanceled: activeTab === ActiveTab.Canceled,
    isOnHold: activeTab === ActiveTab.OnHold
  };

  const sortedColumnsVisibility = columnsVisibility.toSorted((a, b) => {
    const indexA = newOrder.indexOf(a.id);
    const indexB = newOrder.indexOf(b.id);
    return indexA - indexB;
  });

  const hasMultipleCheckedColumns =
    sortedColumnsVisibility.filter((col) => col.checked).length > 1;

  const onResumeHoldSideEffect = () => {
    invalidateQueries?.forEach((queryKey) => {
      queryClient.refetchQueries({
        queryKey
      });
    });
  };

  useEffect(() => {
    setExpanded({});
  }, [applications]);
  const columns = useMemo(() => {
    const ret: ColumnDef<ApplicationWithUIStatus, ApplicationWithUIStatus>[] = [
      {
        id: "fixed_width checkbox custom_",
        header: "",
        size: 40,
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Tooltip placement="top-end">
              <TooltipTrigger>
                <Checkbox
                  wrapperClassName="p-4"
                  {...{
                    checked: row.getIsSelected(),
                    disabled:
                      !row.getCanSelect() ||
                      row.original.status === ApplicationStatus.OnHold,
                    indeterminate: row.getIsSomeSelected(),
                    onChange: row.getToggleSelectedHandler()
                  }}
                />
                <Show if={row.original.status === ApplicationStatus.OnHold}>
                  <TooltipContent className="bg-alpha-800 rounded-lg border p-4 text-sm text-white">
                    Resume this application before selecting it for merging
                  </TooltipContent>
                </Show>
              </TooltipTrigger>
            </Tooltip>
          </div>
        )
      },
      ...(sortedColumnsVisibility
        .map((col) => {
          if (!col.checked || col.disabled) return null;

          if (col.id === "criteria")
            return {
              accessorFn: (row) => row,
              id: `applicant_score ${hasMultipleCheckedColumns ? "custom_ fixed_width" : ""}`,
              header: "Criteria",
              size: 80,
              cell: (p) => {
                const val = p.getValue();
                const templateCriteriaSettings =
                  val.verification_template?.screening_criteria;

                const hasValue =
                  [
                    ApplicationStatus.Submitted,
                    ApplicationStatus.Approved,
                    ApplicationStatus.Rejected,
                    ApplicationStatus.ConditionalApproved,
                    ApplicationStatus.Canceled
                  ].includes(val.status) || p.row.original.submitted_at;

                if (
                  !val.verification_template?.recommendation_method ||
                  val.verification_template.recommendation_method === "none"
                )
                  return null;

                if (
                  val.verification_template.recommendation_method ===
                  "score_based"
                )
                  return (
                    <div className="overflow-hidden pl-2">
                      <Suspense fallback={<Loader />}>
                        <ScoreGraph
                          size={ScoreGraphSize.XS}
                          score={hasValue ? val.score : null}
                          declineRangeEnd={
                            templateCriteriaSettings?.decline_range_end
                          }
                          conditionalApprovalRangeEnd={
                            templateCriteriaSettings?.conditional_approval_range_end
                          }
                        />
                      </Suspense>
                    </div>
                  );

                return (
                  <div className="overflow-hidden pl-6">
                    <RuleBaseGraph
                      recommendation={hasValue ? val.recommendation : null}
                    />
                  </div>
                );
              }
            };

          if (col.id === "applicants")
            return {
              id: "id",
              accessorFn: (row) => row,
              header: "Applicants",
              size: 180,
              cell: (p) => {
                const { applicants, pending_magic_links } = p.getValue();

                const total_applicants =
                  (applicants.length || 0) + (pending_magic_links?.length || 0);

                const applicantNames = getApplicantNames(applicants);
                const inviteesEmails = getInviteesEmails(
                  pending_magic_links || []
                );

                return (
                  <p className="m-0 flex w-full items-center gap-3 2xl:max-w-[400px] ">
                    {total_applicants > 1 ? (
                      <Users01
                        size="20"
                        className="inline shrink-0 stroke-content-300"
                      />
                    ) : (
                      <User01
                        size="20"
                        className="inline shrink-0 stroke-content-300"
                      />
                    )}
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-content-1000">
                      {total_applicants > 1 ? `(${total_applicants}) ` : null}
                      {formatCombinedNamesAndEmails(
                        applicantNames,
                        inviteesEmails
                      )}
                    </span>
                  </p>
                );
              }
            };

          if (col.id === "property" && !pathname.includes("properties"))
            return {
              accessorKey: "property.address.address1",
              header: Property,
              size: 100,
              cell: (p) => {
                const unit = p.row.original;
                return (
                  <span className="block 2xl:max-w-none">
                    <span className="block overflow-hidden text-ellipsis  whitespace-nowrap text-sm font-medium text-content-1000">
                      {unit.property.name}
                    </span>
                    <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-xs text-content-400">
                      {
                        p.getValue() as unknown as Application["property"]["address"]["address1"]
                      }
                    </span>
                  </span>
                );
              }
            };

          if (col.id === "property_group")
            return {
              accessorKey: "property.property_group.name",
              header: `${Property} group`,
              size: 100,
              cell: (p) => {
                return (
                  <span className="block overflow-hidden text-ellipsis whitespace-nowrap 2xl:max-w-[140px] max-w-[80px]">
                    {p.getValue() ? p.getValue() : "--"}
                  </span>
                );
              }
            };

          if (col.id === "unit")
            return {
              accessorKey: "unit.title",
              header: Unit,
              size: 80,
              cell: (p) => {
                return (
                  <span className="block overflow-hidden text-ellipsis whitespace-nowrap 2xl:max-w-[140px] max-w-[80px]">
                    {p.getValue()}
                  </span>
                );
              }
            };

          if (col.id === "submitted_at")
            return {
              accessorKey: "submitted_at",
              header: "Submitted",
              id: "submitted_at fixed_width",
              size: 110,
              cell: (p) => (
                <DateTimeTableCell
                  className="whitespace-nowrap"
                  value={p.getValue()}
                />
              )
            };

          if (col.id === "started_at")
            return {
              accessorKey: "started_at",
              header: "Started",
              id: "started_at fixed_width",
              size: 110,
              cell: (p) => (
                <DateTimeTableCell
                  className="whitespace-nowrap"
                  value={p.getValue()}
                />
              )
            };

          if (col.id === "updated_at")
            return {
              accessorKey: "updated_at",
              header: "Updated at",
              id: "updated_at fixed_width",
              size: 135,
              cell: (p) => (
                <DateTimeTableCell
                  className="whitespace-nowrap"
                  value={p.getValue()}
                />
              )
            };
        })
        .filter(Boolean) as ColumnDef<
        ApplicationWithUIStatus,
        ApplicationWithUIStatus
      >[])
    ];

    ret.push(
      ...[
        ...(sortedColumnsVisibility.find(
          (col) => col.id === "status" && col.checked
        )
          ? [
              {
                accessorKey: "status",
                header: "Application status",
                id: "status",
                size: 130,
                cell: (p) => {
                  const { conditions, status, auto_approved, ui_status } = p.row
                    .original as ApplicationWithUIStatus;

                  const applicationStatus = ui_status;

                  const { applicants } = p.row.original;

                  const isAutoCanceled =
                    status === ApplicationStatus.Canceled &&
                    applicants.every(
                      (applicant) =>
                        applicant.application_status ===
                          ApplicantStatus.Canceled &&
                        applicant.canceled_by_auto_cancel
                    ) &&
                    !applicants.some(
                      (applicant) =>
                        applicant.application_status ===
                        ApplicantStatus.Submitted
                    );

                  return (
                    <div className="flex w-full">
                      <ApplicationStatusBadge
                        status={applicationStatus}
                        conditions={conditions}
                        type="application"
                        isAutoCanceled={isAutoCanceled}
                        autoApproved={auto_approved}
                      />
                    </div>
                  );
                }
              }
            ]
          : []),
        {
          id: "screening_status",
          accessorKey: "screening_status",
          size: 100,
          header: "Screening status",
          cell: (p) => <ScreeningStatusBadge status={p.getValue()} />
        },
        {
          id: "popover-action",
          size: 48,
          accessorFn: (row) => row.id,
          header: "",
          cell: (p) => {
            const id = p.getValue();
            const status = p.row.original.status as ApplicationStatus;
            const actions: {
              title: string | JSX.Element;
              onClick: () => void;
              disabled?: boolean;
              tooltip?: string;
            }[] = [
              {
                title: "Review",
                onClick: () => {
                  navigate(`/applications/${id}`);
                }
              }
            ];

            if (
              ![
                ApplicationStatus.ConditionalApproved,
                ApplicationStatus.Approved,
                ApplicationStatus.Rejected,
                ApplicantStatus.Canceled
              ].includes(status)
            )
              if (status === ApplicationStatus.OnHold)
                actions.push({
                  title: (
                    <span className="flex gap-2 items-center">
                      <HugeiconsIcon
                        icon={PlayIcon}
                        size={20}
                        className="text-content-200 shrink-0"
                      />
                      Resume
                    </span>
                  ),
                  onClick: () => {
                    resumeApplication({
                      applicationId: id,
                      onSuccess: onResumeHoldSideEffect
                    });
                  }
                });
              else {
                const disabled =
                  status !== ApplicationStatus.Submitted ||
                  p.row.getIsSelected();

                let tooltip: string | undefined = undefined;
                if (status !== ApplicationStatus.Submitted)
                  tooltip = "Only submitted applications can be put on hold";
                if (p.row.getIsSelected())
                  tooltip =
                    "Cannot place on hold — application selected for merge";

                actions.push({
                  title: (
                    <span className="flex gap-2 items-center">
                      <HugeiconsIcon
                        icon={PauseIcon}
                        size={20}
                        className="text-content-200 shrink-0"
                      />
                      Place on hold
                    </span>
                  ),
                  disabled,
                  tooltip,
                  onClick: () => {
                    placeOnHoldApplication({
                      applicationId: id,
                      onSuccess: onResumeHoldSideEffect
                    });
                  }
                });
              }

            if (adminPrivileges || permissions.canChangeUnit)
              actions.push({
                title: (
                  <span className="flex gap-2 items-center">
                    <ArrowRight size="18" className="stroke-content-200" />
                    Move to another {unit}
                  </span>
                ),
                tooltip: `You can't move application to another ${unit} once the decision have been made`,
                disabled: [
                  ApplicationStatus.Canceled,
                  ApplicationStatus.Approved,
                  ApplicationStatus.Rejected,
                  ApplicationStatus.ConditionalApproved
                ].includes(p.row.original.status),
                onClick: () => {
                  if (
                    ![
                      ApplicationStatus.Canceled,
                      ApplicationStatus.Approved,
                      ApplicationStatus.Rejected,
                      ApplicationStatus.ConditionalApproved
                    ].includes(p.row.original.status)
                  )
                    onMoveClick && onMoveClick(id);
                }
              });

            actions.push({
              title: (
                <span className="flex gap-2 items-center">
                  <HugeiconsIcon
                    icon={PathfinderDivideIcon}
                    size={18}
                    className="text-content-200 shrink-0"
                  />
                  Split application
                </span>
              ),
              disabled: getApplicationSplittingDisabled(p.row.original),
              tooltip: getApplicationSplittingTooltip(p.row.original),
              onClick: () => onSplitClick && onSplitClick(id)
            });

            if (
              ApplicationStatus.Canceled === status &&
              (adminPrivileges || permissions.canCancelApplications)
            )
              actions.push({
                title: (
                  <span className="flex gap-2 items-center">
                    <Archive size="18" className="stroke-content-200" />
                    Uncancel application
                  </span>
                ),
                disabled: p.row.original.status === ApplicationStatus.OnHold,
                tooltip:
                  "You need to resume the application before uncanceling",
                onClick: () => {
                  onUncancelClick && onUncancelClick(id);
                }
              });

            if (
              ApplicationStatus.Canceled !== status &&
              (adminPrivileges || permissions.canCancelApplications)
            )
              actions.push({
                title: (
                  <span className="flex gap-2 items-center">
                    <Archive size="18" className="stroke-content-200" />
                    Cancel application
                  </span>
                ),
                tooltip:
                  p.row.original.status === ApplicationStatus.Canceled
                    ? "Can not cancel a canceled application"
                    : "You can't cancel application once the decisions have been made",
                disabled: [
                  ApplicationStatus.Canceled,
                  ApplicationStatus.Approved,
                  ApplicationStatus.Rejected,
                  ApplicationStatus.ConditionalApproved
                ].includes(p.row.original.status),
                onClick: () => {
                  if (
                    ![
                      ApplicationStatus.Canceled,
                      ApplicationStatus.Approved,
                      ApplicationStatus.Rejected,
                      ApplicationStatus.ConditionalApproved
                    ].includes(p.row.original.status)
                  )
                    onCancelClick && onCancelClick(id);
                }
              });

            if (adminPrivileges) {
              const { applicants, status } = p.row.original;

              let tooltip: string | undefined = undefined;

              if (
                applicants.some(
                  (applicant: Applicant) =>
                    applicant.application_status === ApplicantStatus.Submitted
                )
              ) {
                const allRefunded = applicants.every(
                  (applicant: Applicant) =>
                    applicant.application_fee_status ===
                    ApplicationFeeStatus.Refunded
                );
                const somePaid = applicants.some(
                  (applicant: Applicant) =>
                    applicant.application_fee_status ===
                    ApplicationFeeStatus.Paid
                );

                if (allRefunded)
                  tooltip =
                    "Refund has already been requested for this application";
                else if (somePaid) tooltip = "";
                else
                  tooltip =
                    "You can request a refund if someone has paid an application fee";
              } else if (status === ApplicationStatus.Canceled)
                tooltip =
                  "You can't request a refund for a canceled application";
              else
                tooltip = "You can request a refund for submitted applications";

              actions.push({
                title: (
                  <span className="flex gap-2 items-center">
                    <HugeiconsIcon
                      icon={CashbackIcon as IconSvgElement}
                      className="text-content-200"
                      size={18}
                    />
                    Refund application
                  </span>
                ),
                disabled: !applicants.some(
                  (applicant: Applicant) =>
                    applicant.application_fee_status ===
                      ApplicationFeeStatus.Paid &&
                    applicant.application_status === ApplicantStatus.Submitted
                ),
                tooltip,
                onClick: () => onRefundClick && onRefundClick(id)
              });
            }

            return <ContextMenuPopover actions={actions} />;
          }
        }
      ]
    );

    return ret;
  }, [currentTab, hasMultipleCheckedColumns]);

  const table = useReactTable<ApplicationWithUIStatus>({
    defaultColumn: {
      size: 100,
      maxSize: 500,
      minSize: 20
    },
    data: tableApplications,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      expanded,
      sorting,
      rowSelection: selectedRentersRow
    },
    enableRowSelection: true,
    manualSorting: true,
    enableMultiSort: false,
    onSortingChange: setSorting,
    // Something is weird
    getSubRows: (rows: ApplicationWithUIStatus) =>
      rows as unknown as ApplicationWithUIStatus[],
    onRowSelectionChange: setSelectedRentersRow,
    onExpandedChange: setExpanded,
    getRowId: (row) => String(row.id)
  });

  const [emptyStateTitle, emptyStateText] = useMemo(() => {
    if (hasSearchParams)
      return [
        "No results found",
        "We couldn't find any data matching your query"
      ];

    if (currentTab.isAll)
      return [
        "Applications will appear here",
        "Invite applicants to create an application"
      ];

    let tabName = activeTab.toLowerCase();
    if (currentTab.isConditionallyApproved) tabName = "conditionally approved";
    else if (currentTab.isOnHold) tabName = "on hold";
    else tabName = capitalize(camelToSentence(tabName));

    const title = `No ${tabName} applications`;

    let prefix: string = activeTab;
    if (currentTab.isConditionallyApproved) prefix = "Conditionally approved";
    else if (currentTab.isOnHold) prefix = "On hold";
    else prefix = capitalize(underscoreToSentence(prefix));

    const text = currentTab.isSubmitted
      ? "Applications awaiting a decision will appear here"
      : `${prefix} applications will appear here`;

    return [title, text];
  }, [currentTab, activeTab, hasSearchParams]);

  const groupByMap = {
    unit: "unit.title",
    status: "ui_status",
    screening_status: "screening_status"
  };
  const EmptyStateIconComponent = useMemo<UntitledIcon>(() => {
    if (currentTab.isSubmitted || currentTab.isAll) return EmptyStateFile06Icon;

    if (hasSearchParams) return EmptyStateSearchIcon;

    return EmptyStateFileXIcon;
  }, [currentTab.isAll, currentTab.isSubmitted, hasSearchParams]);

  return (
    <Table
      groupBy={groupByMap[groupBy || ""]}
      groupByDisplayMap={{
        ui_status: (status) => {
          return (
            <ApplicationStatusBadge
              type="application"
              status={status as ApplicationStatus}
            />
          );
        },
        screening_status: (status) => (
          <ScreeningStatusBadge status={status as string} />
        )
      }}
      rowHeight="64px"
      setSorted={setSorting}
      sorted={sorting}
      sortableKeys={sortableKeys}
      isLoading={isLoading}
      emptyStateTitle={emptyStateTitle}
      emptyStateText={emptyStateText}
      emptyStateButtonText={!currentTab.isAll ? undefined : "Invite applicants"}
      emptyStateButtonAction={
        currentTab.isAll
          ? () => {
              onInviteClick && onInviteClick();
            }
          : undefined
      }
      EmptyStateIcon={EmptyStateIconComponent}
      table={table}
      onRowClick={(id) => navigate(`/applications/${id}`)}
      mobileRowClassName="max-w-[calc(100vw-1.5rem)]"
      mobileRow={(row: {
        original: ApplicationWithUIStatus;
        getIsSelected: () => boolean;
        getCanSelect: () => boolean;
        getIsSomeSelected: () => boolean;
        getToggleSelectedHandler: () => () => void;
      }) => {
        const applicants: (Invitee | Applicant)[] =
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          (row.original.applicants || [])
            // @ts-expect-error
            .concat(row.original.pending_magic_links || []);
        const names = applicants
          .map((applicant) =>
            "first_name" in applicant
              ? getFullName({
                  firstName: applicant.first_name,
                  lastName: applicant.last_name,
                  middleName: applicant.middle_name,
                  short: true
                })
              : applicant.email
          )
          .join(", ");
        return (
          <div>
            <div className="flex items-center justify-between">
              <div
                className="h-5 -translate-y-5"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Checkbox
                  {...{
                    checked: row.getIsSelected(),
                    disabled: !row.getCanSelect(),
                    indeterminate: row.getIsSomeSelected(),
                    onChange: row.getToggleSelectedHandler()
                  }}
                  label={
                    <div>
                      {((): ReactNode => {
                        const val = row.original;
                        const templateCriteriaSettings =
                          val.verification_template?.screening_criteria;

                        const hasValue = [
                          ApplicationStatus.Submitted,
                          ApplicationStatus.Approved,
                          ApplicationStatus.Rejected,
                          ApplicationStatus.ConditionalApproved,
                          ApplicationStatus.Canceled
                        ].includes(val.status);

                        if (
                          !val.verification_template?.recommendation_method ||
                          val.verification_template.recommendation_method ===
                            "none"
                        )
                          return null;

                        if (
                          val.verification_template.recommendation_method ===
                          "score_based"
                        )
                          return (
                            <div className="overflow-hidden">
                              <Suspense fallback={<Loader />}>
                                <ScoreGraph
                                  size={ScoreGraphSize.XS}
                                  score={hasValue ? val.score : null}
                                  declineRangeEnd={
                                    templateCriteriaSettings?.decline_range_end
                                  }
                                  conditionalApprovalRangeEnd={
                                    templateCriteriaSettings?.conditional_approval_range_end
                                  }
                                />
                              </Suspense>
                            </div>
                          );

                        return (
                          <div className="overflow-hidden mx-auto flex w-full 2xl:justify-center 2xl:pl-0 justify-center">
                            <RuleBaseGraph
                              recommendation={
                                hasValue ? val.recommendation : null
                              }
                            />
                          </div>
                        );
                      })()}
                    </div>
                  }
                />
              </div>

              <ContextMenuPopover
                triggerClassName="translate-x-2"
                actions={[
                  {
                    title: "Review",
                    onClick: () => {
                      navigate(`/applications/${row.original.id}`);
                    }
                  }
                ]}
              />
            </div>

            <div className="flex flex-col w-full">
              <div className="flex w-full items-center gap-2">
                <p className="text-sm font-medium text-content-1000">
                  {applicants.length > 1
                    ? `(${row.original.applicants.length}) `
                    : ""}
                  {names}
                </p>
              </div>
              <div className="flex w-full items-center gap-2">
                <p className="text-xs text-content-400">
                  {row.original.property.address.address1} /{" "}
                  {row.original.unit.title}
                </p>
              </div>
            </div>

            <div className="mt-2 ml-auto">
              <ApplicationStatusBadge
                status={row.original.ui_status}
                type="application"
                autoApproved={row.original.auto_approved}
              />
            </div>
          </div>
        );
      }}
      // expandedRenderer={(row) => {
      //   const data = row.originalSubRows as unknown as Application | undefined;
      //   if (!data)
      //     return (
      //       <div className="animate-appear flex w-full flex-wrap gap-4 bg-background-900">
      //         <div className="w-full max-w-[270px] rounded-lg border border-alpha-100 bg-white p-6 shadow-xs">
      //           <h2 className="line-clamp-1 text-ellipsis text-sm font-bold text-content-1000">
      //             No data
      //           </h2>
      //         </div>
      //       </div>
      //     );
      //   return (
      //     <div className="flex-wrap flex gap-2 p-4">
      //       {(data.applicants || [])
      //         .concat((data.pending_magic_links || []) as Applicant[])
      //         .map((applicant: Applicant | Invitee) => {
      //           return (
      //             <ApplicantCard
      //               applicationStatus={data.status}
      //               refunded={data.refunded}
      //               invalidateQueries={invalidateQueries}
      //               key={
      //                 "expanded_application_" +
      //                 data.id +
      //                 "_applicant" +
      //                 applicant.id
      //               }
      //               applicationId={String(data.id)}
      //               applicant={applicant}
      //               criteriaEnabled={Boolean(
      //                 data.verification_template?.recommendation_method ===
      //                   "score_based"
      //               )}
      //               templateCriteriaSettings={
      //                 data.verification_template?.screening_criteria
      //               }
      //             />
      //           );
      //         })}
      //     </div>
      //   );
      // }}
    />
  );
};
